import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const SettingsContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCY_CONFIG = {
  USD: { symbol: '$', name: 'US Dollar', locale: 'en-US' },
  EUR: { symbol: '€', name: 'Euro', locale: 'de-DE' },
  AED: { symbol: 'د.إ', name: 'UAE Dirham', locale: 'ar-AE' }
};

export const SettingsProvider = ({ children }) => {
  const { token } = useAuth();
  const [settings, setSettings] = useState({
    currency: 'USD',
    currency_symbol: '$',
    company_name: '911 Pro Home Live'
  });
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateSettings = async (newSettings) => {
    try {
      const response = await axios.put(`${API}/settings`, newSettings);
      setSettings(response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const formatCurrency = (value) => {
    const config = CURRENCY_CONFIG[settings.currency] || CURRENCY_CONFIG.USD;
    
    // For AED, format differently
    if (settings.currency === 'AED') {
      return `${config.symbol} ${value.toFixed(2)}`;
    }
    
    return new Intl.NumberFormat(config.locale, {
      style: 'currency',
      currency: settings.currency
    }).format(value);
  };

  const getCurrencySymbol = () => {
    return settings.currency_symbol || CURRENCY_CONFIG[settings.currency]?.symbol || '$';
  };

  return (
    <SettingsContext.Provider value={{
      settings,
      loading,
      updateSettings,
      formatCurrency,
      getCurrencySymbol,
      refreshSettings: fetchSettings,
      CURRENCY_CONFIG
    }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
